import type { ReactElement, Ref } from 'react';
import { useEffect, useState } from 'react';

import { type IWithPopoverProps, WithPopover } from '@procurify/ui'
import { isFunction } from 'lodash'

import { CommentsAPI } from '@webapp/api/CommentsAPI'
import { type Comment as CommentType } from '@webapp/models/Comment'
import { Comment, type ICommentProps } from './Comment'

export interface ICommentPopoverProps
  extends ICommentProps,
    Partial<IWithPopoverProps> {
  fetchOnMount?: boolean
  ref?: Ref<WithPopover>
  children?:
    | ((commentsCount: number) => ReactElement)
    | ReactElement
  readOnly?: boolean
  pageSize?: number
}

export const WithCommentPopover = (props: ICommentPopoverProps) => {
  const {
    children,
    contentID,
    objectID,
    userID,
    onCommentUpdate,
    fetchOnMount,
    readOnly,
    pageSize,
    ...otherProps
  } = props

  const [comments, setComments] = useState<CommentType[]>([])

  // Initial fetch the comments to get comment count
  useEffect(() => {
    if (fetchOnMount) {
      CommentsAPI.getComments(contentID, objectID, { page_size: pageSize })
        .then((res) => res.data)
        .then((data) => {
          setComments(data)
          onCommentUpdate(data)
        })
    }
  }, [])

  const handleCommentUpdate = (newComments: CommentType[]) => {
    setComments(newComments)
    onCommentUpdate(newComments)
  }

  const renderComment = () => (
    <div>
      <Comment
        contentID={contentID}
        objectID={objectID}
        userID={userID}
        onCommentUpdate={handleCommentUpdate}
        initialComments={comments}
        readOnly={readOnly}
        pageSize={pageSize}
      />
    </div>
  )

  return (
    <WithPopover {...otherProps} content={renderComment()}>
      {isFunction(children) ? children(comments.length) : children}
    </WithPopover>
  )
}

WithCommentPopover.defaultProps = {
  fetchOnMount: true,
  trigger: 'click',
  placement: 'bottom',
  onCommentUpdate: () => undefined,
  readOnly: false,
}

WithCommentPopover.displayName = 'WithCommentPopover'
